import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import MatterService from '@frontend/AppSettings/services/MatterService';
import { Matter } from '@frontend/Models/Matter/MatterModels';
import { subDays, startOfDay, differenceInHours } from 'date-fns';
import {
  isEngineeringEnvironment,
  isLegalEnvironment,
} from '@frontend/AppSettings/environmentConfig';
import './ActiveWorkloadWidget.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export interface ActiveWorkloadWidgetProps {
  className?: string;
}

/**
 * ActiveWorkloadWidget Component
 * 
 * Displays a donut chart showing matter/project status distribution:
 * - Completed (green) - completed matters/projects from the last 90 days
 * - In Progress (blue) - matters/projects due 24+ hours away
 * - At Risk (red) - matters/projects due within 24 hours or overdue
 * 
 * Features:
 * - Fetches matter/project data from MatterService
 * - Calculates status based on due dates (closedate) with 24-hour threshold
 * - Uses same logic as MattersTable for consistency
 * - Displays summary cards below the chart
 * - Responsive design
 */
/** Matter-like shape used for status buckets; both Matter and engineering project have closedate + status */
type MatterLike = Pick<Matter, 'status' | 'closedate'> & { matterid?: string };

const ActiveWorkloadWidget: React.FC<ActiveWorkloadWidgetProps> = ({
  className = '',
}) => {
  const navigate = useNavigate();

  // Fetch only the dataset for the current environment (same conditional pattern as App.js routes)
  const { data: matters = [], isPending: mattersPending, error: mattersError } = useQuery<MatterLike[]>({
    queryKey: ['matters', 'all', isLegalEnvironment, isEngineeringEnvironment],
    queryFn: async () => {
      if (isLegalEnvironment) {
        const mattersData = await MatterService.getMatters();
        return mattersData ?? [];
      }
      if (isEngineeringEnvironment) {
        const projectsData = await MatterService.getProjectMatters();
        return Array.isArray(projectsData) ? projectsData : [];
      }
      return [];
    },
    enabled: isLegalEnvironment || isEngineeringEnvironment,
  });

  const isPending = mattersPending;
  const error = mattersError;

  // Handle navigation to Matters/Projects page with workload filter
  // Navigate to appropriate page based on environment
  const handleWorkloadClick = (workloadType: 'completed' | 'in-progress' | 'at-risk') => {
    const basePath = isEngineeringEnvironment ? '/projects' : '/matters';
    navigate(`${basePath}?workloadFilter=${workloadType}&view=team`);
  };

  // Calculate status distribution based on due dates (same logic as MattersTable)
  const statusData = useMemo(() => {
    if (!matters || matters.length === 0) {
      return {
        completed: 0,
        inProgress: 0,
        atRisk: 0,
        total: 0,
      };
    }

    const now = new Date();
    const ninetyDaysAgo = subDays(startOfDay(now), 90);

    const statusCounts = matters.reduce(
      (acc: { completed: number; inProgress: number; atRisk: number; total: number }, matter: MatterLike) => {
        const status = (matter.status || '').trim().toLowerCase();
        const isCompleted = status === 'completed' || status === 'closed';
        let isCompletedInLast90Days = false;

        // Check if matter is completed within last 90 days
        if (isCompleted && matter.closedate) {
          try {
            const closeDate = startOfDay(new Date(matter.closedate));
            const nowDate = startOfDay(now);
            if (closeDate >= ninetyDaysAgo && closeDate <= nowDate) {
              isCompletedInLast90Days = true;
              acc.completed++;
            }
          } catch (e) {
            // Invalid date, skip
          }
        }

        // Only categorize non-completed matters
        if (!isCompletedInLast90Days) {
          // Check due date (closedate) for at-risk/in-progress categorization
          // Uses same logic as MattersTable.js
          if (matter.closedate) {
            try {
              const dueDate = new Date(matter.closedate);
              const hoursUntilDue = differenceInHours(dueDate, now);
              
              // At Risk: due within 24 hours or past due (same as MattersTable)
              if (hoursUntilDue <= 24) {
                acc.atRisk++;
              }
              // In Progress: due 24+ hours away
              else {
                acc.inProgress++;
              }
            } catch (e) {
              // Invalid date, count as in progress
              acc.inProgress++;
            }
          } else {
            // No due date, count as in progress
            acc.inProgress++;
          }
        }

        acc.total++;
        return acc;
      },
      { completed: 0, inProgress: 0, atRisk: 0, total: 0 }
    );

    return statusCounts;
  }, [matters]);

  // Prepare chart data
  const chartData = {
    labels: ['Completed', 'In Progress', 'At Risk'],
    datasets: [
      {
        data: [
          statusData.completed,
          statusData.inProgress,
          statusData.atRisk,
        ],
        backgroundColor: ['#28a745', '#007bff', '#dc3545'],
        borderColor: ['#ffffff', '#ffffff', '#ffffff'],
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const percentage = statusData.total > 0 
              ? ((value / statusData.total) * 100).toFixed(1) 
              : '0.0';
            return `${label} ${percentage}%`;
          },
        },
      },
    },
    cutout: '60%',
  };

  if (isPending) {
    return (
      <div className={`active-workload-widget ${className}`}>
        <div className="active-workload-header">
          <h3 className="active-workload-title">Active Workload</h3>
        </div>
        <div className="active-workload-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`active-workload-widget ${className}`}>
        <div className="active-workload-header">
          <h3 className="active-workload-title">Active Workload</h3>
        </div>
        <div className="active-workload-error">Error loading data</div>
      </div>
    );
  }

  return (
    <div className={`active-workload-widget ${className}`}>
      <div className="active-workload-header">
        <h3 className="active-workload-title">Active Workload</h3>
      </div>
      
      <div className="active-workload-content">
        {/* Donut Chart */}
        <div className="active-workload-chart-wrapper">
          <div className="active-workload-chart-container">
            <Doughnut data={chartData} options={chartOptions} />
            <div className="active-workload-chart-center">
              <div className="active-workload-total">{statusData.total}</div>
              <div className="active-workload-total-label">
                {isEngineeringEnvironment ? 'Total Projects' : 'Total Matters'}
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="active-workload-summary">
          <div 
            className="active-workload-summary-card completed clickable"
            onClick={() => handleWorkloadClick('completed')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleWorkloadClick('completed');
              }
            }}
            aria-label={`View ${statusData.completed} completed ${isEngineeringEnvironment ? 'projects' : 'matters'}`}
          >
            <div className="active-workload-summary-count">{statusData.completed}</div>
            <div className="active-workload-summary-label">Completed</div>
          </div>
          
          <div 
            className="active-workload-summary-card in-progress clickable"
            onClick={() => handleWorkloadClick('in-progress')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleWorkloadClick('in-progress');
              }
            }}
            aria-label={`View ${statusData.inProgress} in progress ${isEngineeringEnvironment ? 'projects' : 'matters'}`}
          >
            <div className="active-workload-summary-count">{statusData.inProgress}</div>
            <div className="active-workload-summary-label">In Progress</div>
          </div>
          
          <div 
            className="active-workload-summary-card at-risk clickable"
            onClick={() => handleWorkloadClick('at-risk')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleWorkloadClick('at-risk');
              }
            }}
            aria-label={`View ${statusData.atRisk} at risk ${isEngineeringEnvironment ? 'projects' : 'matters'}`}
          >
            <div className="active-workload-summary-count">{statusData.atRisk}</div>
            <div className="active-workload-summary-label">At Risk</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActiveWorkloadWidget;

